import { LoadingOutlined, PlusOutlined } from '@ant-design/icons'
import { Button, Spin } from 'antd'
import { FigureSelectionArea } from 'features/fde/regionTypes'
import {
  IndicationItem,
  useUpdateIndicationContext,
} from 'features/indicationTriage'
import find from 'lodash/find'
import React, {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
} from 'react'
import styled from 'styled-components'
import { MathUtils } from 'three'

import {
  HighlightOptions,
  IndicationTriageHighlightType,
  IndicationTriageListRow,
} from './IndicationTriageListRow'

function createBlankIndicationItem(
  selectedComponentId: string | null | undefined,
  indicationSetId: string
): IndicationItem {
  return {
    id: MathUtils.generateUUID(),
    componentId: selectedComponentId ?? null,
    title: 'New Indication',
    selection: undefined,
    detail: undefined,
    indicationSetId,
  }
}

// - - - - - - - - - - - - - - - - Indication Triage List - - - - - - - - - - - - - - - -
const IndicationTriageListContainer = styled.div`
  display: grid;
  width: 100%;
  gap: 12px;
  align-items: center;

  /* Use auto-fit with minmax to create responsive columns */
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));

  /* Adjust the minimum width (150px in this case) to control the column size */
  @media (min-width: 400px) {
    grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  }

  @media (min-width: 900px) {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  }

  @media (min-width: 1200px) {
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  }
`

export interface IndicationTriageListProps {
  selectedComponentId?: string
  items: ReadonlyArray<IndicationItem>
  indicationSetId?: string
  highlight?: IndicationTriageHighlightType | null
  setHighlight?: (highlight: IndicationTriageHighlightType | null) => void
  highlightOptions?: HighlightOptions
  classificationTooltipPrefix?: string
  loading?: boolean
  setActiveDetailsItemId?: (itemId: string | null) => void
  allowCreateOrChangeRegion?: boolean
  rectSelection?: FigureSelectionArea | null
  updatingRegionItemId?: string | null
  setUpdatingRegionItemId?: Dispatch<SetStateAction<string | null>>
}

const AddButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  margin: 0.5rem;
`

export const IndicationTriageList: FC<IndicationTriageListProps> = (props) => {
  const {
    selectedComponentId,
    items,
    highlight,
    setHighlight,
    highlightOptions,
    loading,
    classificationTooltipPrefix,
    setActiveDetailsItemId,
    allowCreateOrChangeRegion,
    rectSelection,
    updatingRegionItemId,
    setUpdatingRegionItemId,
    indicationSetId,
  } = props

  const { updateOrAddIndication, setSelection } = useUpdateIndicationContext()
  useEffect(() => {
    if (rectSelection && updatingRegionItemId) {
      try {
        const selectionForItem = find(
          items,
          (item) => item.id === updatingRegionItemId
        )
        if (!selectionForItem) {
          console.warn(`No item found for id ${updatingRegionItemId}`)
          return
        }
        updateOrAddIndication?.({
          ...selectionForItem,
          selection: rectSelection,
        })
      } finally {
        setUpdatingRegionItemId?.(null)
        setSelection?.(undefined)
        setHighlight?.({ id: updatingRegionItemId, highlight: 'outline' })
      }
    }
  }, [
    items,
    rectSelection,
    setHighlight,
    setSelection,
    setUpdatingRegionItemId,
    updateOrAddIndication,
    updatingRegionItemId,
  ])
  // Memoize this to protect the <Card> from re-renders

  const rowSetHighlight = useCallback(
    (
      id: string,
      _highlight: IndicationTriageHighlightType['highlight'] | null
    ) => setHighlight?.(_highlight ? { id, highlight: _highlight } : null),
    [setHighlight]
  )
  const rowSetRegionUpdateInProgress = useMemo(
    () =>
      updateOrAddIndication
        ? (id: string, inProgress: boolean) =>
            setUpdatingRegionItemId?.(inProgress ? id : null)
        : undefined,
    [setUpdatingRegionItemId, updateOrAddIndication]
  )
  const rowSetDetailsActive = useMemo(
    () =>
      setActiveDetailsItemId
        ? (id: string) => setActiveDetailsItemId(id)
        : undefined,
    [setActiveDetailsItemId]
  )
  return (
    <>
      <Spin
        indicator={<LoadingOutlined />}
        size='large'
        spinning={!!loading}
        style={{ minHeight: '50vh' }}>
        {!loading && (
          <IndicationTriageListContainer>
            {items.map((item) => {
              return (
                <IndicationTriageListRow
                  key={item.id}
                  highlight={
                    highlight?.id === item.id ? highlight.highlight : null
                  }
                  item={item}
                  setHighlight={rowSetHighlight}
                  setRegionUpdateInProgress={rowSetRegionUpdateInProgress}
                  selectedComponentId={selectedComponentId}
                  regionUpdateInProgress={
                    !!updatingRegionItemId && updatingRegionItemId === item.id
                  }
                  highlightOptions={highlightOptions}
                  classificationTooltipPrefix={classificationTooltipPrefix}
                  onCardClick={rowSetDetailsActive}
                  allowCreateOrChangeRegion={allowCreateOrChangeRegion}
                />
              )
            })}
          </IndicationTriageListContainer>
        )}
      </Spin>
      {allowCreateOrChangeRegion !== false &&
        updateOrAddIndication &&
        indicationSetId && (
          <AddButtonContainer>
            <Button
              shape='circle'
              onClick={() => {
                const newItem = createBlankIndicationItem(
                  selectedComponentId,
                  indicationSetId
                )
                updateOrAddIndication(newItem)
              }}>
              <PlusOutlined />
            </Button>
          </AddButtonContainer>
        )}
    </>
  )
}
